import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { memo } from "react";
import { Handle, Position } from "@xyflow/react";
import { useTranslation } from "react-i18next";
import { HelpCircle } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "../../../../base/avatar/avatar";
import { getInitials, getUserAvatarUrl } from "../../../../../utils/user-utils";
import { useNodeVisualState } from "../hooks/useNodeVisualState";
import { proposedEntityPalette } from "../../../step-detail/suggestionNames";
export const VIRAL_PLACEHOLDER_WIDTH = 200;
export const VIRAL_PLACEHOLDER_HEIGHT = 44;
const EMERALD_PALETTE = {
    border: "border-positive-solid",
    bg: "bg-positive-surface",
    text: "text-positive-strong",
    avatar: "bg-positive-surface-strong text-positive-strong text-xs"
};
// Amber palette: mirrors the UNCLEAR end-node styling so reviewers can see
// at a glance which placeholders still need owner resolution.
const AMBER_PALETTE = {
    border: "border-warning-solid",
    bg: "bg-warning-surface",
    text: "text-warning-strong",
    avatar: "bg-warning-surface-strong text-warning-strong text-xs"
};
// Proposed (blue) / declined (red) chip palette, derived from the shared
// `proposedEntityPalette` token so the canvas chip and the step-detail panels
// can't drift apart. Mapped onto this node's `{ border, bg, text, avatar }`
// shape: the canvas uses the denser `-900` name shade (`nodeText`) where the
// panels use `text`. EMERALD (default pending) and AMBER (unclear) stay local —
// they aren't the "proposed" concept.
function proposedChipPalette(isDeclined) {
    const p = proposedEntityPalette(isDeclined);
    return {
        border: p.border,
        bg: p.bg,
        text: p.nodeText,
        avatar: p.avatar
    };
}
/**
 * Aria-label i18n key for a viral placeholder, by precedence: declined
 * (rejected suggestion) → proposed (pending suggestion) → unclear escalation →
 * accepted → pending. The suggestion-derived states (declined/proposed) take
 * precedence because they describe the assignee itself; `isAccepted` already
 * implies an open-assignment, so the kind check folds away.
 */ function viralPlaceholderAriaKey(flags) {
    if (flags.isDeclined) return "processFlow.viralPlaceholder.declined";
    if (flags.isProposed) return "processFlow.viralPlaceholder.proposed";
    if (flags.isPendingUnclear) return "processFlow.viralPlaceholder.pendingUnclear";
    if (flags.isAccepted) return "processFlow.viralPlaceholder.accepted";
    return "processFlow.viralPlaceholder.pending";
}
function ProcessFlowViralPlaceholderNode({ data }) {
    const { t } = useTranslation();
    const isEdit = data?.mode === "edit";
    const { state, tokens, dimClass, handleMouseEnter, handleMouseLeave } = useNodeVisualState({
        isSelected: data?.isSelected,
        isCurrentStep: data?.isCurrentStep,
        isDimmed: data?.isDimmed,
        isEdit,
        hasPublishError: data?.hasPublishError
    });
    if (!data) return null;
    const isPendingUnclear = data.status === "pending-unclear";
    const isUnclearVariant = data.variant === "unclear";
    if (isUnclearVariant) {
        const ariaLabel = t("processFlow.viralPlaceholder.pendingUnclear");
        return /*#__PURE__*/ _jsxs("div", {
            className: `relative transition-opacity duration-200 ${dimClass}`,
            style: {
                width: VIRAL_PLACEHOLDER_WIDTH,
                height: VIRAL_PLACEHOLDER_HEIGHT
            },
            "aria-label": ariaLabel,
            onMouseEnter: handleMouseEnter,
            onMouseLeave: handleMouseLeave,
            children: [
                /*#__PURE__*/ _jsx(Handle, {
                    type: "target",
                    position: Position.Left,
                    style: {
                        top: VIRAL_PLACEHOLDER_HEIGHT / 2
                    },
                    isConnectable: isEdit
                }),
                /*#__PURE__*/ _jsx("div", {
                    className: `flex items-center justify-center rounded-full border-2 border-dashed ${AMBER_PALETTE.border} ${AMBER_PALETTE.bg} ${tokens.ringClass} ${tokens.markerRingClass}`,
                    style: {
                        width: VIRAL_PLACEHOLDER_WIDTH,
                        height: VIRAL_PLACEHOLDER_HEIGHT
                    },
                    children: /*#__PURE__*/ _jsx(HelpCircle, {
                        size: 24,
                        className: "text-warning pointer-events-none"
                    })
                })
            ]
        });
    }
    const isProposed = Boolean(data.assignee?.proposed);
    const isDeclined = Boolean(data.assignee?.declined);
    // The proposed/declined chip palette (declined takes precedence — it implies
    // proposed). Null when the assignee is neither, so the default/unclear/brand
    // branches apply.
    const proposedChip = isProposed || isDeclined ? proposedChipPalette(isDeclined) : null;
    // Non-default interaction states (hover/active/currentStep) flow from the
    // shared visual tokens for parity with other node types. At rest, red signals
    // a declined (rejected) suggestion assignee, blue a still-pending proposed
    // one, amber a PENDING_UNCLEAR escalation, emerald the brand default.
    const palette = state !== "default" ? {
        border: tokens.borderClass,
        bg: tokens.bgClass,
        text: tokens.textClass,
        avatar: proposedChip?.avatar ?? EMERALD_PALETTE.avatar
    } : proposedChip ? proposedChip : isPendingUnclear ? AMBER_PALETTE : EMERALD_PALETTE;
    // 'accepted' is the only state with a confirmed recipient → solid border.
    // Everything else (pending, pending-unclear, viral-step author has not
    // dispatched yet) reads as in-flight and gets a dashed border.
    const isAccepted = data.kind === "open-assignment" && data.status === "accepted";
    const dashed = !isAccepted;
    const avatarUrl = getUserAvatarUrl(data.assignee ?? undefined, "thumbnail");
    const initials = getInitials(data.assignee?.name);
    const displayName = data.assignee?.name ?? "";
    const ariaLabel = t(viralPlaceholderAriaKey({
        isPendingUnclear,
        isProposed,
        isDeclined,
        isAccepted
    }));
    return /*#__PURE__*/ _jsxs("div", {
        className: `relative transition-opacity duration-200 ${dimClass}`,
        style: {
            width: VIRAL_PLACEHOLDER_WIDTH,
            height: VIRAL_PLACEHOLDER_HEIGHT
        },
        "aria-label": ariaLabel,
        onMouseEnter: handleMouseEnter,
        onMouseLeave: handleMouseLeave,
        children: [
            /*#__PURE__*/ _jsx(Handle, {
                type: "target",
                position: Position.Left,
                style: {
                    top: VIRAL_PLACEHOLDER_HEIGHT / 2
                },
                isConnectable: isEdit
            }),
            isEdit && data.kind === "assignment" && /*#__PURE__*/ _jsx(Handle, {
                type: "target",
                position: Position.Left,
                id: "editor-target",
                style: {
                    top: VIRAL_PLACEHOLDER_HEIGHT / 2,
                    opacity: 0
                }
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: `flex items-center gap-2 rounded-full px-2 border-2 ${palette.border} ${palette.bg} ${dashed ? "border-dashed" : "border-solid"} ${tokens.ringClass} ${tokens.markerRingClass}`,
                style: {
                    width: VIRAL_PLACEHOLDER_WIDTH,
                    height: VIRAL_PLACEHOLDER_HEIGHT
                },
                children: [
                    /*#__PURE__*/ _jsxs(Avatar, {
                        className: "h-7 w-7 shrink-0",
                        children: [
                            avatarUrl ? /*#__PURE__*/ _jsx(AvatarImage, {
                                src: avatarUrl,
                                alt: displayName
                            }) : null,
                            /*#__PURE__*/ _jsx(AvatarFallback, {
                                className: palette.avatar,
                                children: initials
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx("span", {
                        className: `truncate text-xs font-medium ${palette.text}`,
                        children: displayName
                    })
                ]
            }),
            isPendingUnclear && /*#__PURE__*/ _jsx("div", {
                className: "absolute -top-2 -left-2 flex h-5 w-5 items-center justify-center rounded-full bg-warning-solid text-white shadow",
                "aria-hidden": true,
                children: /*#__PURE__*/ _jsx(HelpCircle, {
                    size: 14
                })
            })
        ]
    });
}
export default /*#__PURE__*/ memo(ProcessFlowViralPlaceholderNode);

//# sourceMappingURL=ViralPlaceholderNode.js.map